import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { Timeline } from '@xxtract/ui';
import { useOrderTree } from '../hooks/useOrderTree';
import { useOrderTimeline } from '../hooks/useOrderTimeline';
import { OrderTreeView } from '../components/OrderTreeView';
import { OrderDetailHeader } from '../components/OrderDetailHeader';
import { toTimelineItems } from '../timeline-mapper';
import { deriveLeadTimeMs, deriveSubmittedAt } from '../order-summary';

function DegradedBlock({ title, description, retryLabel, onRetry }: { title: string; description: string; retryLabel: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-[var(--border)] py-12 text-center" data-testid="degraded-block">
      <span aria-hidden="true" className="material-symbols-outlined text-[32px] text-[var(--warning)]">cloud_off</span>
      <p className="text-sm font-semibold text-[var(--foreground)]">{title}</p>
      <p className="max-w-md text-xs text-[var(--foreground-muted)]">{description}</p>
      <button className="text-xs font-medium text-[var(--secondary)] underline" onClick={onRetry} type="button">{retryLabel}</button>
    </div>
  );
}

function CardHeader({ icon, title, subtitle }: { icon: string; title: string; subtitle?: string }) {
  return (
    <div className="flex items-center gap-2 border-b border-[var(--border)] px-5 py-3.5">
      <span aria-hidden="true" className="material-symbols-outlined text-[18px] text-[var(--foreground-muted)]">{icon}</span>
      <h2 className="text-sm font-semibold text-[var(--foreground)]">{title}</h2>
      {subtitle != null ? <span className="ml-auto text-xs text-[var(--foreground-muted)]">{subtitle}</span> : null}
    </div>
  );
}

export function OrderDetailPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const treeQuery = useOrderTree(orderNumber ?? '');
  const timelineQuery = useOrderTimeline(orderNumber ?? '');

  function openGtinDetail(tradeItemId: string) {
    navigate(`/orders/${orderNumber}/items/${tradeItemId}`);
  }

  function backToOrders() {
    navigate('/orders');
  }

  const tree = treeQuery.data?.data ?? null;
  const treeDegraded = treeQuery.data?.meta.degraded ?? false;
  const timelineEvents = timelineQuery.data?.data ?? null;
  const timelineDegraded = timelineQuery.data?.meta.degraded ?? false;

  const productCount = tree?.products.length ?? 0;
  const layerCount = tree?.products.reduce((total, product) => total + product.packagingLayers.length, 0) ?? 0;
  const treeCountLabel = tree != null
    ? `${t('orders.detail.productCount', { count: productCount })} · ${t('orders.detail.layerCount', { count: layerCount })}`
    : undefined;

  const submittedAt = timelineEvents != null ? deriveSubmittedAt(timelineEvents) : null;
  const leadTimeMs = timelineEvents != null ? deriveLeadTimeMs(timelineEvents) : null;

  return (
    <div className="space-y-5" data-testid="order-detail-page">
      {tree != null ? (
        <OrderDetailHeader leadTimeMs={leadTimeMs} locale={i18n.language} onBack={backToOrders} orderNumber={orderNumber ?? ''} submittedAt={submittedAt} tree={tree} />
      ) : (
        <header>
          <h1 className="text-xl font-bold tracking-tight text-[var(--foreground)]">{t('orders.detail.title', { orderNumber })}</h1>
        </header>
      )}

      <div className="grid gap-5 lg:grid-cols-[3fr_2fr]">
        <section aria-label={t('orders.detail.treeSectionLabel')} className="overflow-hidden rounded-xl border border-[var(--border)]" data-testid="order-tree-section">
          <CardHeader icon="package_2" subtitle={treeCountLabel} title={t('orders.detail.productsTitle')} />
          {treeQuery.isLoading ? (
            <div className="space-y-2 p-4" data-testid="order-tree-loading">
              {Array.from({ length: 4 }).map((_, index) => <div className="skeleton h-10 rounded-lg" key={index} />)}
            </div>
          ) : treeDegraded ? (
            <div className="p-4"><DegradedBlock description={t('orders.detail.treeDegraded.description')} onRetry={() => treeQuery.refetch()} retryLabel={t('common.retry')} title={t('orders.detail.treeDegraded.title')} /></div>
          ) : tree == null || tree.products.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[var(--border)] p-4 py-12 text-center" data-testid="order-tree-empty">
              <p className="text-sm text-[var(--foreground-muted)]">{t('orders.detail.treeEmpty')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <OrderTreeView locale={i18n.language} onOpenGtinDetail={openGtinDetail} tree={tree} />
            </div>
          )}
        </section>

        <section aria-label={t('orders.detail.timelineSectionLabel')} className="overflow-hidden rounded-xl border border-[var(--border)]" data-testid="order-timeline-section">
          <CardHeader icon="timeline" title={t('orders.detail.timelineSectionLabel')} />
          {timelineQuery.isLoading ? (
            <div className="space-y-2 p-4" data-testid="order-timeline-loading">
              {Array.from({ length: 4 }).map((_, index) => <div className="skeleton h-8 rounded-lg" key={index} />)}
            </div>
          ) : timelineDegraded ? (
            <div className="p-4"><DegradedBlock description={t('orders.detail.timelineDegraded.description')} onRetry={() => timelineQuery.refetch()} retryLabel={t('common.retry')} title={t('orders.detail.timelineDegraded.title')} /></div>
          ) : timelineEvents == null || timelineEvents.length === 0 ? (
            <div className="p-4">
              <div className="rounded-xl border border-dashed border-[var(--border)] py-12 text-center" data-testid="order-timeline-empty">
                <p className="text-sm text-[var(--foreground-muted)]">{t('orders.detail.timelineEmpty')}</p>
              </div>
            </div>
          ) : (
            <div className="p-4">
              <Timeline
                ariaLabel={t('orders.detail.timelineSectionLabel')}
                data-testid="order-timeline"
                items={toTimelineItems(timelineEvents, i18n.language, t)}
                showAllLabel={t('common.showMore')}
                showLessLabel={t('common.showLess')}
              />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
